import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  FunnelAnalysisResponse,
  getGiftEventFunnel,
} from '@shared/api/giftEventLog/giftEventLog.controller';
import { Loading } from '@shared/components/Loading';

type RangePreset = 'today' | '7d' | '30d' | 'all' | 'custom';

const toDateInputValue = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDateRange = (range: RangePreset) => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  let start: Date | undefined;

  switch (range) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case '7d':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      break;
    case '30d':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      break;
    default:
      start = undefined;
  }

  return {
    startDate: start?.toISOString(),
    endDate: end.toISOString(),
  };
};

const dateToStartISO = (dateStr: string) => {
  return new Date(`${dateStr}T00:00:00`).toISOString();
};

const dateToEndISO = (dateStr: string) => {
  return new Date(`${dateStr}T23:59:59.999`).toISOString();
};

const FunnelBar: FC<{
  label: string;
  count: number;
  logCount: number;
  maxCount: number;
  color: string;
  revenue?: number;
}> = ({ label, count, logCount, maxCount, color, revenue }) => {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <div className="text-right">
          <span className="font-bold text-gray-900">
            {count}명
            <span className="ml-1 text-xs font-normal text-gray-500">({logCount}회)</span>
          </span>
          {revenue != null && revenue > 0 && (
            <div className="text-xs font-bold text-green-600">
              {revenue.toLocaleString()}원
            </div>
          )}
        </div>
      </div>
      <div className="relative h-8 w-full rounded-full bg-gray-100">
        <div
          className={`absolute left-0 top-0 flex h-full items-center rounded-full px-3 text-xs font-bold text-white transition-all duration-500 ${color}`}
          style={{ width: `${Math.max(pct, 2)}%` }}
        >
          {pct >= 10 && `${pct.toFixed(1)}%`}
        </div>
        {pct < 10 && (
          <span
            className="absolute top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500"
            style={{ left: `calc(${Math.max(pct, 2)}% + 8px)` }}
          >
            {pct.toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
};

export const GiftEventLogPage: FC = function GiftEventLogPage() {
  const [range, setRange] = useState<RangePreset>('7d');
  const [customStart, setCustomStart] = useState(() => toDateInputValue(new Date()));
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue(new Date()));
  const [appliedStart, setAppliedStart] = useState('');
  const [appliedEnd, setAppliedEnd] = useState('');
  const [data, setData] = useState<FunnelAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const [showItemDetail, setShowItemDetail] = useState(false);
  const [showPurchaseDetail, setShowPurchaseDetail] = useState(false);

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    try {
      let startDate: string | undefined;
      let endDate: string | undefined;
      if (range === 'custom') {
        startDate = dateToStartISO(appliedStart);
        endDate = dateToEndISO(appliedEnd);
      } else {
        const dates = getDateRange(range);
        startDate = dates.startDate;
        endDate = dates.endDate;
      }
      const result = await getGiftEventFunnel(startDate, endDate);
      setData(result);
    } catch (error) {
      console.error('퍼널 분석 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [range, appliedStart, appliedEnd]);

  useEffect(() => {
    if (range !== 'custom') {
      fetchFunnel();
    }
  }, [range, fetchFunnel]);

  const handleApplyCustom = useCallback(() => {
    setAppliedStart(customStart);
    setAppliedEnd(customEnd);
  }, [customStart, customEnd]);

  useEffect(() => {
    if (range === 'custom' && appliedStart && appliedEnd) {
      fetchFunnel();
    }
  }, [range, appliedStart, appliedEnd, fetchFunnel]);

  const funnelData = useMemo(() => {
    if (!data) return [];
    const { funnel, logCounts, purchaseSummary } = data;

    const counts = [
      funnel.courtesyView,
      funnel.storeView,
      funnel.itemView,
      funnel.purchased,
    ];
    const maxCount = Math.max(...counts, 1);

    const pct = (a: number, b: number) => (b > 0 ? (a / b) * 100 : 0);

    return [
      {
        label: '① 답례글 페이지 진입',
        count: funnel.courtesyView,
        logCount: logCounts.courtesyView,
        maxCount,
        color: 'bg-blue-500',
        detailType: undefined as string | undefined,
        revenue: undefined as number | undefined,
        bugoPct: pct(funnel.courtesyView, funnel.totalBugos),
        stepPct: null as number | null,
      },
      {
        label: '② 답례품 목록 진입',
        count: funnel.storeView,
        logCount: logCounts.storeView,
        maxCount,
        color: 'bg-purple-500',
        detailType: undefined as string | undefined,
        revenue: undefined as number | undefined,
        bugoPct: pct(funnel.storeView, funnel.totalBugos),
        stepPct: pct(funnel.storeView, funnel.courtesyView),
      },
      {
        label: '③ 상품 상세 조회',
        count: funnel.itemView,
        logCount: logCounts.itemView,
        maxCount,
        color: 'bg-orange-500',
        detailType: 'itemView' as string | undefined,
        revenue: undefined as number | undefined,
        bugoPct: pct(funnel.itemView, funnel.totalBugos),
        stepPct: pct(funnel.itemView, funnel.storeView),
      },
      {
        label: '④ 구매 완료',
        count: funnel.purchased,
        logCount: purchaseSummary.purchasedOrders,
        maxCount,
        color: 'bg-green-500',
        detailType: 'purchase' as string | undefined,
        revenue: purchaseSummary.purchasedRevenue,
        bugoPct: pct(funnel.purchased, funnel.totalBugos),
        stepPct: pct(funnel.purchased, funnel.itemView),
      },
    ];
  }, [data]);

  const conversionRates = useMemo(() => {
    if (!data) return [];
    const { funnel } = data;
    const rate = (a: number, b: number) => (b > 0 ? ((a / b) * 100).toFixed(1) : '-');
    return [
      {
        label: '답례글 → 답례품 목록',
        value: rate(funnel.storeView, funnel.courtesyView),
      },
      {
        label: '답례품 목록 → 상세',
        value: rate(funnel.itemView, funnel.storeView),
      },
      {
        label: '상세 → 구매',
        value: rate(funnel.purchased, funnel.itemView),
      },
      {
        label: '전체 전환율 (답례글→구매)',
        value: rate(funnel.purchased, funnel.courtesyView),
      },
    ];
  }, [data]);

  const eventCounts = useMemo(() => {
    if (!data) return [];
    const { funnel, logCounts } = data;
    return [
      {
        label: '답례글 페이지',
        color: 'bg-blue-100 text-blue-800',
        bugoCount: funnel.courtesyView,
        logCount: logCounts.courtesyView,
      },
      {
        label: '답례품 목록',
        color: 'bg-purple-100 text-purple-800',
        bugoCount: funnel.storeView,
        logCount: logCounts.storeView,
      },
      {
        label: '상품 상세',
        color: 'bg-orange-100 text-orange-800',
        bugoCount: funnel.itemView,
        logCount: logCounts.itemView,
      },
    ];
  }, [data]);

  if (loading || !data) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  const { funnel, purchaseSummary, itemViewDetails, purchaseDetails } = data;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">이벤트 로그</h1>
        <p className="mt-1 text-sm text-gray-500">
          답례품 구매 전 사용자 행동 퍼널 분석 (부고 단위 집계)
        </p>
      </div>

      {/* 기간 필터 */}
      <div className="mb-6 space-y-3">
        <div className="flex flex-wrap gap-2">
          {(
            [
              { key: 'today', label: '오늘' },
              { key: '7d', label: '7일' },
              { key: '30d', label: '30일' },
              { key: 'all', label: '전체' },
              { key: 'custom', label: '직접선택' },
            ] as const
          ).map((item) => (
            <button
              key={item.key}
              onClick={() => setRange(item.key)}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                range === item.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        {range === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <span className="text-sm text-gray-500">~</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              onClick={handleApplyCustom}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              적용
            </button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* 요약 카드 */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <div className="rounded-lg bg-white p-4 shadow">
            <p className="text-xs text-gray-500">고유 부고 수</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{funnel.totalBugos}명</p>
            {funnel.totalFevents > 0 && (
              <p className="mt-1 text-xs text-gray-500">{funnel.totalFevents}상가</p>
            )}
          </div>
          {funnelData.map((item) => (
            <div key={item.label} className="rounded-lg bg-white p-4 shadow">
              <p className="text-xs text-gray-500">{item.label}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {item.count}명
                <span className="ml-1 text-sm font-normal text-gray-500">
                  ({item.logCount}회)
                </span>
              </p>
              <div className="mt-1.5 space-y-0.5">
                <p className="text-xs text-gray-500">
                  부고 대비{' '}
                  <span className="font-bold text-indigo-600">
                    {item.bugoPct.toFixed(1)}%
                  </span>
                </p>
                {item.stepPct != null && (
                  <p className="text-xs text-gray-500">
                    이전 단계 대비{' '}
                    <span className="font-bold text-amber-600">
                      {item.stepPct.toFixed(1)}%
                    </span>
                  </p>
                )}
              </div>
              {item.revenue != null && item.revenue > 0 && (
                <p className="mt-1 text-sm font-bold text-green-600">
                  {item.revenue.toLocaleString()}원
                </p>
              )}
            </div>
          ))}
        </div>

        {/* 퍼널 바 차트 */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-bold text-gray-800">퍼널 (부고 단위)</h2>
          <div className="space-y-4">
            {funnelData.map((item) => (
              <div key={item.label}>
                <FunnelBar {...item} />
                {item.detailType === 'itemView' && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowItemDetail((prev) => !prev)}
                      className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      <span>{showItemDetail ? '▼' : '▶'}</span>
                      <span>상품별 상세보기 ({itemViewDetails.length}개 상품)</span>
                    </button>
                    {showItemDetail && (
                      <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        {itemViewDetails.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            상품 상세 조회 기록이 없습니다.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            <div className="mb-2 flex items-center justify-between text-xs font-medium text-gray-500">
                              <span>상품명 / 가격</span>
                              <span>조회 수</span>
                            </div>
                            {itemViewDetails.map((detail) => {
                              const maxDetailCount = itemViewDetails[0]?.bugoCount || 1;
                              const pct = (detail.bugoCount / maxDetailCount) * 100;
                              return (
                                <div key={detail.giftItemId}>
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex max-w-[60%] items-baseline gap-2">
                                      <span
                                        className="truncate text-gray-700"
                                        title={detail.giftItemName}
                                      >
                                        {detail.giftItemName}
                                      </span>
                                      {detail.priceRetail > 0 && (
                                        <span className="shrink-0 text-xs text-gray-400">
                                          {detail.priceRetail.toLocaleString()}원
                                        </span>
                                      )}
                                    </div>
                                    <span className="font-bold text-gray-900">
                                      {detail.bugoCount}명
                                      <span className="ml-1 text-xs font-normal text-gray-500">
                                        ({detail.logCount}회)
                                      </span>
                                    </span>
                                  </div>
                                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                    <div
                                      className="h-full rounded-full bg-orange-400 transition-all duration-300"
                                      style={{ width: `${Math.max(pct, 2)}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {item.detailType === 'purchase' && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowPurchaseDetail((prev) => !prev)}
                      className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      <span>{showPurchaseDetail ? '▼' : '▶'}</span>
                      <span>상품별 상세보기 ({purchaseDetails.length}개 상품)</span>
                    </button>
                    {showPurchaseDetail && (
                      <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        {purchaseDetails.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            구매 완료 기록이 없습니다.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            <div className="mb-2 flex items-center justify-between text-xs font-medium text-gray-500">
                              <span>상품명 / 가격</span>
                              <span>수량 / 매출</span>
                            </div>
                            {purchaseDetails.map((detail) => {
                              const maxDetailCount = purchaseDetails[0]?.bugoCount || 1;
                              const pct = (detail.bugoCount / maxDetailCount) * 100;
                              return (
                                <div key={detail.giftItemId}>
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex max-w-[50%] items-baseline gap-2">
                                      <span
                                        className="truncate text-gray-700"
                                        title={detail.giftItemName}
                                      >
                                        {detail.giftItemName}
                                      </span>
                                      {detail.priceRetail > 0 && (
                                        <span className="shrink-0 text-xs text-gray-400">
                                          {detail.priceRetail.toLocaleString()}원
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <span className="font-bold text-gray-900">
                                        {detail.quantity}개
                                      </span>
                                      <span className="ml-1 text-xs font-normal text-gray-500">
                                        ({detail.bugoCount}명 / {detail.orderCount}건)
                                      </span>
                                      <div className="text-xs text-green-600">
                                        {detail.revenue.toLocaleString()}원
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        객단가{' '}
                                        {detail.bugoCount > 0
                                          ? Math.round(
                                              detail.revenue / detail.bugoCount,
                                            ).toLocaleString()
                                          : 0}
                                        원/명 ·{' '}
                                        {detail.orderCount > 0
                                          ? Math.round(
                                              detail.revenue / detail.orderCount,
                                            ).toLocaleString()
                                          : 0}
                                        원/건
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                    <div
                                      className="h-full rounded-full bg-green-400 transition-all duration-300"
                                      style={{ width: `${Math.max(pct, 2)}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {(() => {
                              const totalQty = purchaseDetails.reduce(
                                (s, d) => s + d.quantity,
                                0,
                              );
                              const totalRevenue = purchaseDetails.reduce(
                                (s, d) => s + d.revenue,
                                0,
                              );
                              const totalOrders = purchaseDetails.reduce(
                                (s, d) => s + d.orderCount,
                                0,
                              );
                              return (
                                <div className="mt-3 flex items-center justify-between border-t border-gray-300 pt-3">
                                  <span className="text-sm font-bold text-gray-700">
                                    합계
                                  </span>
                                  <div className="text-right">
                                    <span className="font-bold text-gray-900">
                                      {totalQty.toLocaleString()}개
                                    </span>
                                    <div className="text-sm font-bold text-green-600">
                                      {totalRevenue.toLocaleString()}원
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      객단가{' '}
                                      {purchaseSummary.purchasedBugos > 0
                                        ? Math.round(
                                            totalRevenue / purchaseSummary.purchasedBugos,
                                          ).toLocaleString()
                                        : 0}
                                      원/명 ·{' '}
                                      {totalOrders > 0
                                        ? Math.round(
                                            totalRevenue / totalOrders,
                                          ).toLocaleString()
                                        : 0}
                                      원/건
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 전환율 */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-bold text-gray-800">단계별 전환율</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {conversionRates.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between rounded-lg border border-gray-100 p-4"
              >
                <span className="text-sm text-gray-600">{item.label}</span>
                <span className="text-lg font-bold text-indigo-600">
                  {item.value === '-' ? '-' : `${item.value}%`}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 이벤트별 집계 */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-bold text-gray-800">
            이벤트별 총 로그 수 (중복 포함)
          </h2>
          <div className="space-y-2">
            {eventCounts.map((ec) => (
              <div key={ec.label} className="flex items-center justify-between">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${ec.color}`}
                >
                  {ec.label}
                </span>
                <span className="font-bold text-gray-900">
                  {ec.bugoCount}명
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    ({ec.logCount}회)
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
